import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Component & Asset Imports
import Toptext from '../../components/Toptext';
import Globe from '../../assets/icons/globe.svg';
import Button1 from '../../components/ui/Button1';
import DynamicHeading from '../../components/ui/CustomHeading';
import { FaSuitcase, FaBolt, FaUsers, FaCheckCircle } from 'react-icons/fa';
import SubHeading from '../../components/ui/SubHeading';

gsap.registerPlugin(ScrollTrigger);

const vacancies = [
    {
        id: 1,
        title: 'Senior Robotics Engineer',
        location: 'London, UK',
        type: 'Full-time',
        description: 'Lead the design and development of our next-generation robotic systems.'
    },
    {
        id: 2,
        title: 'AI/ML Research Scientist',
        location: 'Mumbai, India',
        type: 'Full-time',
        description: 'Develop advanced algorithms for machine learning, computer vision, and autonomous navigation.'
    },
    {
        id: 3,
        title: 'Junior Automation Technician',
        location: 'Remote',
        type: 'Contract',
        description: 'Assist in the deployment and maintenance of automated solutions for our global clients.'
    },
];

const CareersPage = () => {
    const mainRef = useRef(null);


    useEffect(() => {
        document.title = 'Careers | JBS Capacitors';
        window.scrollTo(0, 0);
    }, []);

    useGSAP(() => {
        // Hero Section Intro
        const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
        tl.from('.anim-toptext', { y: -30, opacity: 0 })
          .from('.anim-main-heading span', { y: 40, opacity: 0, stagger: 0.1 }, "-=0.5")
          .from('.anim-subtext', { y: 20, opacity: 0 }, "-=0.6")
          .from('.anim-button', { scale: 0.8, opacity: 0 }, "-=0.5");

        // Animate sections on scroll
        const animateOnScroll = (selector) => {
            gsap.from(selector, {
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: selector,
                    start: 'top 85%',
                    toggleActions: 'play none none none'
                }
            });
        };

        ['.anim-vacancies', '.anim-why-join-us', '.anim-contact'].forEach(sel => {
            const el = document.querySelector(sel);
            if (el) animateOnScroll(el);
        });
    }, { scope: mainRef });

    return (
        <div ref={mainRef} className='mt-32 flex flex-col items-center justify-center overflow-x-hidden'>
            {/* Top Heading */}
            <div className="anim-toptext">
                <Toptext icon={Globe}>
                    Join Our Mission — Building the Future of Robotics and AI
                </Toptext>
            </div>

            {/* Main Section */}
            <div className='mt-12 text-center px-4'>
                <div className="anim-main-heading">
                    <DynamicHeading
                        parts={[
                            { text: 'Join Our ' },
                            { text: 'Team,', highlight: true },
                            { lineBreak: true },
                            { text: 'Shape the Future' },
                        ]}
                    />
                </div>
                <div className="anim-subtext">
                    <SubHeading>
                        Be part of a team that's revolutionizing robotics, AI, and automation.
                        We're looking for passionate individuals who want to make a real impact.
                    </SubHeading>
                </div>
                <div className='mt-10 anim-button'>
                    <a href="#vacancies">
                        <Button1 width={"200px"}>
                            View Openings
                        </Button1>
                    </a>
                </div>
            </div>

            {/* Current Vacancies Section */}
            <div id="vacancies" className='w-full mt-24 text-center anim-vacancies'>
                <DynamicHeading
                    parts={[
                        { text: 'Current' },
                        { text: 'Vacancies', highlight: true },
                    ]}
                />

                <div className='mt-12 max-w-4xl mx-auto px-4'>
                    {vacancies.length > 0 ? (
                        <div className='space-y-6 text-left'>
                            {vacancies.map((job) => (
                                <motion.div
                                    key={job.id}
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    viewport={{ once: true }}
                                    className='bg-white p-6 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center'
                                >
                                    <div>
                                        <h3 className='text-xl font-bold text-jbsBlue'>{job.title}</h3>
                                        <p className='text-gray-500 mt-1'>{job.location} • {job.type}</p>
                                        <p className='text-gray-600 mt-2 hidden sm:block'>{job.description}</p>
                                    </div>
                                    <a href="#apply" className='mt-4 sm:mt-0 sm:ml-4 flex-shrink-0'>
                                        <Button1 width={"140px"}>
                                            Apply Now
                                        </Button1>
                                    </a>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className='bg-gray-50 rounded-[38px] p-12 border-2 border-dashed border-gray-300'>
                            <div className='text-center'>
                                <div className='w-24 h-24 bg-gray-200 rounded-full mx-auto mb-6 flex items-center justify-center'>
                                    <FaSuitcase className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className='text-2xl font-bold text-jbsGray mb-2'>
                                    Currently No Vacancies
                                </h3>
                                <p className='text-gray-600 text-lg mb-6'>
                                    We don't have any open positions right now, but we're always looking for talented individuals.
                                </p>
                                <p className='text-gray-500 text-sm'>
                                    Feel free to send us your resume, and we'll keep you in mind for future opportunities.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Why Join Us Section */}
            <div className='w-full mt-24 text-center bg-gray-50 py-20 anim-why-join-us'>
                <DynamicHeading
                    parts={[
                        { text: 'Why Join ' },
                        { text: 'Us?', highlight: true },
                    ]}
                />

                <div className='mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4'>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className='bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300'
                    >
                        <div className='w-16 h-16 bg-jbsBlue rounded-full mx-auto mb-4 flex items-center justify-center'>
                            <FaBolt className="w-8 h-8 text-white" />
                        </div>
                        <h3 className='text-xl font-bold text-jbsGray mb-3'>Innovation First</h3>
                        <p className='text-gray-600'>
                            Work on cutting-edge robotics and AI technologies that are shaping the future of automation.
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                        className='bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300'
                    >
                        <div className='w-16 h-16 bg-jbsBlue rounded-full mx-auto mb-4 flex items-center justify-center'>
                            <FaUsers className="w-8 h-8 text-white" />
                        </div>
                        <h3 className='text-xl font-bold text-jbsGray mb-3'>Global Team</h3>
                        <p className='text-gray-600'>
                            Join a diverse team with operations in India and the UK, working on international projects.
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        viewport={{ once: true }}
                        className='bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300'
                    >
                        <div className='w-16 h-16 bg-jbsBlue rounded-full mx-auto mb-4 flex items-center justify-center'>
                            <FaCheckCircle className="w-8 h-8 text-white" />
                        </div>
                        <h3 className='text-xl font-bold text-jbsGray mb-3'>Growth Opportunities</h3>
                        <p className='text-gray-600'>
                            Continuous learning and career development in a fast-growing technology company.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Contact Section */}
            <div id="apply" className='mt-24 text-center px-4 mb-20 anim-contact'>
                <DynamicHeading
                    parts={[
                        { text: 'Get in ' },
                        { text: 'Touch', highlight: true },
                    ]}
                />

                <div className='mt-6 max-w-2xl mx-auto'>
                    <SubHeading>
                        Interested in joining our team? Send us your resume and a cover letter explaining why you'd be a great fit.
                    </SubHeading>
                    <div className='mt-6'>
                        <Button1 width={"300px"}>
                            <a href="mailto:hr@yourcompany.com">Send Your Resume</a>
                        </Button1>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CareersPage;
